import React, { useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  invResetFieldsState,
  inventoryModalTitleState,
  toastBgState,
  toastMessageState,
} from "../../utils/atoms/atoms";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../common/CustomTable";
import { InventoryColumns } from "../../utils/table/inventoryCols";
import { getInventory, getInventoryVendor } from "../../api/inventory";
import CustomModal from "../CustomModal";
import InventoryFormBody from "./InventoryFormBody";
import ToastNotification from "../common/ToastNotification";
import LoadingComponent from "../../LoadingComponent";
import PartInOutModalBody from "./PartInOutModalBody";
import { HasRole } from "../../context/HasRole";
import LocationModalBody from "./LocationModalBody";
import PartyModalBody from "./PartyModalBody";
import AdditionalInfoModal from "./AdditionalInfoModalBody";
import { InventoryTanotiColumns } from "../../utils/table/inventoryTanotiCols";
import InventoryTanotiFormBody from "./InventoryTanotiFormBody";
import InOutFormBody from "./InOutFormBody";
import ToolingsDashboard from "../tooling/ToolingsDashboard";
import ToolingImportButton from "../tooling/ToolingImportButton";

import InventoryDownloadButton, {
  InventoryCategory,
} from "./InventoryDownloadButton";

const InventoryDashboard = () => {
  const {
    isLoading,
    data: inventory,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: ["inventory"],
    queryFn: getInventory,
  });

  const {
    isLoading: isLoadingVendor,
    data: inventoryVendor,
    isError: isErrorVendor,
    isSuccess: isSuccessVendor,
  } = useQuery({
    queryKey: ["inventory-vendor"],
    queryFn: getInventoryVendor,
  });
  const setInvResetFields = useSetRecoilState(invResetFieldsState);
  const queryClient = useQueryClient();
  const [inventoryType, setInventoryType] =
    useState<InventoryCategory>("client");
  const resetInventoryFormMutation = () => {
    queryClient.removeQueries({ queryKey: ["inventory-item"] });
  };

  const toastBg = useRecoilValue(toastBgState);
  const toastMessage = useRecoilValue(toastMessageState);
  const [inventoryModalCategory, setInventoryModalCategory] = useState("");
  const [inventoryModalTitle, setInventoryModalTitle] = useRecoilState(
    inventoryModalTitleState
  );
  const [isLocation, setIsLocation] = useState(false);

  const isErrorVal = isError || isErrorVendor;
  const isSuccessVal = isSuccess && isSuccessVendor;
  const isLoadingVal = isLoading || isLoadingVendor;

  return (
    <LoadingComponent
      isError={isErrorVal}
      isSuccess={isSuccessVal}
      isLoading={isLoadingVal}
      ComponentBody={
        <div className="inventory-page">
          <div className="inventory-header">
            <HasRole roles={["admin", "accounts", "production", "viewer"]}>
              <div className="inventory-toolbar">
                <div
                  className="inventory-tabs d-flex flex-wrap gap-2"
                  role="group"
                  aria-label="Inventory category"
                >
                  <button
                    type="button"
                    className={`styled-btn${
                      inventoryType === "client" ? "--dark" : "--std"
                    } me-2 `}
                    onClick={() => {
                      setInventoryType("client");
                    }}
                  >
                    Client Items
                  </button>
                  <button
                    type="button"
                    className={`styled-btn${
                      inventoryType === "vendor" ? "--dark" : "--std"
                    } me-2 `}
                    onClick={() => {
                      setInventoryType("vendor");
                    }}
                  >
                    Vendor Items
                  </button>
                  <button
                    type="button"
                    className={`styled-btn${
                      inventoryType === "toolings" ? "--dark" : "--std"
                    } me-2`}
                    onClick={() => setInventoryType("toolings")}
                  >
                    Toolings
                  </button>
                </div>
                <div className="d-flex flex-wrap gap-2 inventory-actions">
                  <InventoryDownloadButton
                    key={inventoryType}
                    category={inventoryType}
                  />
                  {inventoryType === "toolings" && (
                    <HasRole roles={["admin", "accounts", "production"]}>
                      <ToolingImportButton />
                    </HasRole>
                  )}
                  {inventoryType !== "toolings" && (
                    <HasRole roles={["admin", "accounts", "production"]}>
                      <div
                        className="styled-btn--std me-2 mobile-d-none"
                        data-bs-toggle="modal"
                        data-bs-target="#exampleModal"
                        onClick={() => {
                          resetInventoryFormMutation();
                          setInvResetFields(true);
                          setInventoryModalTitle("Add Inventory");
                          setInventoryModalCategory("inv");
                        }}
                      >
                        + Add
                      </div>
                      <div
                        className="styled-btn--std me-2 mobile-d-none"
                        data-bs-toggle="modal"
                        data-bs-target="#locModal"
                        onClick={() => {
                          setInventoryModalCategory("loc");
                          setIsLocation(true);
                          setInventoryModalTitle("Add Location");
                        }}
                      >
                        + Location
                      </div>
                      <div
                        className="styled-btn--std me-2 mobile-d-none"
                        data-bs-toggle="modal"
                        data-bs-target="#parModal"
                        onClick={() => {
                          setInventoryModalCategory("par");
                          setIsLocation(false);
                          setInventoryModalTitle("Add Party");
                        }}
                      >
                        + Party
                      </div>
                    </HasRole>
                  )}
                </div>
              </div>
            </HasRole>
          </div>
          {inventoryType === "toolings" ? (
            <ToolingsDashboard />
          ) : (
            <CustomTable
              data={inventoryType === "client" ? inventory : inventoryVendor}
              columns={
                process.env.REACT_APP_COMPANY_NAME === "TANOTI"
                  ? InventoryTanotiColumns
                  : InventoryColumns
              }
              isFilter={true}
              isPaginate={true}
            />
          )}
          <CustomModal
            modalTitle={inventoryModalTitle}
            modalId="locModal"
            modalBody={<LocationModalBody />}
          />
          <CustomModal
            modalTitle={inventoryModalTitle}
            modalId="additionalInfoModal"
            modalBody={<AdditionalInfoModal />}
          />
          <CustomModal
            modalTitle={inventoryModalTitle}
            modalBody={<PartyModalBody />}
            modalId="parModal"
          />
          <CustomModal
            modalId="inOutModal"
            modalTitle={inventoryModalTitle}
            modalBody={<InOutFormBody />}
          />
          <CustomModal
            modalTitle={inventoryModalTitle}
            modalBody={
              process.env.REACT_APP_COMPANY_NAME === "TANOTI" ? (
                <InventoryTanotiFormBody />
              ) : (
                <InventoryFormBody />
              )
            }
          />
          <CustomModal
            modalTitle={inventoryModalTitle}
            modalBody={<PartInOutModalBody />}
            modalId="historyModal"
          />
          <ToastNotification bg={toastBg} message={toastMessage} />
        </div>
      }
    />
  );
};

export default InventoryDashboard;
